// The RunDiffusion mark as the app's loader (components/base/MarkLoader.vue):
// its four facets, and the clock every loader in the window shares. The
// board's bundle keeps a twin of this file (web/board/src/ui/rdMark.ts) for
// its own loader; keep the two in step.

/** the mark's own box, cropped to its shapes (as the top bar's mark) */
export const MARK_VIEWBOX = '1 33 239 173';

export interface MarkFacet {
	d: string;
	fill: string;
	/** the facet's centre in the mark's box, where it turns */
	origin: string;
	/** the facet's long axis, which it turns about */
	axis: [number, number];
	/** from the mark's centre out through the facet: where it turns in from */
	out: [number, number];
}

// Clockwise from the top, the order they turn in and out. The three facet
// directions are the hex grid's (30°, 150° and upright), so each turns about
// its own length like a blade.
export const MARK_FACETS: MarkFacet[] = [
	{
		d: 'M160.973 91.1044H105.586C103.271 91.1044 101.134 89.8671 99.9766 87.8597L74.5215 43.7307C72.0284 39.4055 75.1448 33.9999 80.1311 33.9999H135.518C137.833 33.9999 139.97 35.2372 141.128 37.2447L166.579 81.3772C169.072 85.7024 165.956 91.108 160.97 91.108L160.973 91.1044Z',
		fill: '#CC5FD1',
		origin: '50% 17.1%',
		axis: [0.87, 0.49],
		out: [0, -1],
	},
	{
		d: 'M150.485 148.213H205.872C208.187 148.213 210.324 146.975 211.481 144.968L236.933 100.835C239.426 96.5102 236.31 91.1046 231.323 91.1046H175.936C173.621 91.1046 171.484 92.3419 170.327 94.3494L144.875 138.482C142.382 142.807 145.498 148.213 150.485 148.213Z',
		fill: '#9A6ADF',
		origin: '79.5% 50.1%',
		axis: [0.87, -0.49],
		out: [1, 0],
	},
	{
		d: 'M160.974 205.317H105.587C103.271 205.317 101.134 204.079 99.9769 202.072L74.5254 157.939C72.0323 153.614 75.1487 148.209 80.135 148.209H135.522C137.837 148.209 139.974 149.446 141.132 151.453L166.583 195.586C169.076 199.911 165.96 205.317 160.974 205.317Z',
		fill: '#B0B3F6',
		origin: '50% 83.1%',
		axis: [0.87, 0.49],
		out: [0, 1],
	},
	{
		d: 'M40.7394 200.572L67.1953 151.857C68.2994 149.821 68.2389 147.35 67.0279 145.371L40.4581 101.905C37.8545 97.6479 31.6216 97.8048 29.2389 102.194L2.78656 150.909C1.68245 152.945 1.74299 155.416 2.95395 157.395L29.5238 200.86C32.1274 205.118 38.3603 204.961 40.743 200.572H40.7394Z',
		fill: '#A691DF',
		origin: '14.2% 68%',
		axis: [0, 1],
		out: [-0.94, 0.34],
	},
];

/** the whole mark as a CSS mask, for the light that passes over it */
export const MARK_MASK = `url("data:image/svg+xml,${encodeURIComponent(
	`<svg xmlns='http://www.w3.org/2000/svg' viewBox='${MARK_VIEWBOX}'>${MARK_FACETS.map((f) => `<path d='${f.d}'/>`).join('')}</svg>`,
)}")`;

/** each facet's place in the turn, as the CSS custom properties its keyframes read */
export function facetStyle(f: MarkFacet, i: number): Record<string, string> {
	return {
		'--i': String(i),
		'--ox': f.origin.split(' ')[0],
		'--oy': f.origin.split(' ')[1],
		'--ax': String(f.axis[0]),
		'--ay': String(f.axis[1]),
		'--dx': String(f.out[0]),
		'--dy': String(f.out[1]),
	};
}

// One clock for every mark loader in the window, the web app's and the
// board's: a loader that takes over from another (the app's boot screen, the
// page waiting for the board's bundle, the board opening) carries on from the
// same frame instead of starting the turn again. It restarts only once no
// loader has been on screen for a moment (a fading one still counts).
interface MarkLoaderClock {
	epoch: number;
	live: number;
	lastSeen: number;
}
declare global {
	interface Window {
		__rdMarkLoaderClock?: MarkLoaderClock;
	}
}
const RESTART_AFTER_MS = 800;

/** joins the clock; returns how far into the turn a loader mounting now starts, in ms */
export function joinMarkLoaderClock(): number {
	const now = performance.now();
	const clock = window.__rdMarkLoaderClock ?? (window.__rdMarkLoaderClock = { epoch: now, live: 0, lastSeen: -Infinity });
	if (clock.live === 0 && now - clock.lastSeen > RESTART_AFTER_MS) clock.epoch = now;
	clock.live++;
	return Math.round(now - clock.epoch);
}

export function leaveMarkLoaderClock(): void {
	const clock = window.__rdMarkLoaderClock;
	if (!clock) return;
	clock.live = Math.max(0, clock.live - 1);
	clock.lastSeen = performance.now();
}
